import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  artifacts?: Artifact[];
}

export interface Artifact {
  id: string;
  type: 'chart' | 'table' | 'summary';
  title: string;
  data?: unknown;
}

export interface Conversation {
  id: string;
  title: string;
  preview: string;
  updatedAt: Date;
  messages: ChatMessage[];
  artifacts: Artifact[];
}

interface DataCoachChatContextValue {
  conversations: Conversation[];
  selectedConversation: Conversation | null;
  isLoading: boolean;
  selectConversation: (id: string) => void;
  createNewConversation: () => void;
  sendMessage: (content: string) => Promise<void>;
}

const DataCoachChatContext = createContext<DataCoachChatContextValue | null>(
  null
);

const MOCK_CONVERSATIONS: Conversation[] = [
  {
    id: '1',
    title: 'Q4 Revenue Insights',
    preview: "Let's analyze the Q4 revenue data...",
    updatedAt: new Date(),
    messages: [
      {
        id: '1',
        role: 'user',
        content:
          'Can you help me analyze the Q4 revenue data? I need to understand the trends and identify any anomalies.',
        timestamp: new Date(Date.now() - 3600000),
      },
      {
        id: '2',
        role: 'assistant',
        content:
          "I'd be happy to help you analyze the Q4 revenue data. Let me start by examining the key metrics and trends.\n\nI've identified several important patterns in your data:\n\n- Revenue increased 23% compared to Q3\n- December showed unusually high activity at 40% above average\n- 3 irregular segments contributed 67% of total growth",
        timestamp: new Date(Date.now() - 3500000),
        artifacts: [
          { id: 'a1', type: 'chart', title: 'Q4 Revenue Chart' },
          { id: 'a2', type: 'chart', title: 'Monthly Breakdown' },
        ],
      },
      {
        id: '3',
        role: 'user',
        content:
          'Can you create a visualization showing the month-by-month breakdown?',
        timestamp: new Date(Date.now() - 3400000),
      },
    ],
    artifacts: [
      { id: 'a1', type: 'chart', title: 'Q4 Revenue Chart' },
      { id: 'a2', type: 'chart', title: 'Monthly Breakdown' },
      { id: 'a3', type: 'summary', title: 'Analysis Summary' },
    ],
  },
  {
    id: '2',
    title: 'Customer Churn Prediction',
    preview: 'Build a model to identify at-risk...',
    updatedAt: new Date(Date.now() - 86400000),
    messages: [],
    artifacts: [],
  },
  {
    id: '3',
    title: 'Sales Dashboard Setup',
    preview: 'Help set up a real-time sales...',
    updatedAt: new Date(Date.now() - 172800000),
    messages: [],
    artifacts: [],
  },
  {
    id: '4',
    title: 'Data Pipeline Optimization',
    preview: 'Review ETL process efficiency...',
    updatedAt: new Date(Date.now() - 259200000),
    messages: [],
    artifacts: [],
  },
  {
    id: '5',
    title: 'Marketing Attribution Model',
    preview: 'Create multi-touch attribution...',
    updatedAt: new Date(Date.now() - 345600000),
    messages: [],
    artifacts: [],
  },
];

export const DataCoachChatProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { threadId } = useParams<{ threadId?: string }>();
  const navigate = useNavigate();

  const [conversations, setConversations] =
    useState<Conversation[]>(MOCK_CONVERSATIONS);
  const [isLoading, setIsLoading] = useState(false);

  // Navigate to the first conversation if no threadId is in the URL
  useEffect(() => {
    if (!threadId && conversations.length > 0) {
      navigate(conversations[0].id, { replace: true });
    }
  }, [threadId, conversations, navigate]);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === threadId) ?? null,
    [conversations, threadId]
  );

  const selectConversation = useCallback(
    (id: string) => {
      navigate(`/organizations/chat/${id}`);
    },
    [navigate]
  );

  const createNewConversation = useCallback(() => {
    const newConversation: Conversation = {
      id: `new-${Date.now()}`,
      title: 'New Conversation',
      preview: '',
      updatedAt: new Date(),
      messages: [],
      artifacts: [],
    };
    setConversations((prev) => [newConversation, ...prev]);
    navigate(`/organizations/chat/${newConversation.id}`);
  }, [navigate]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!threadId) return;

      const userMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content,
        timestamp: new Date(),
      };

      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === threadId
            ? {
                ...conv,
                messages: [...conv.messages, userMessage],
                preview: content.slice(0, 50),
                updatedAt: new Date(),
              }
            : conv
        )
      );

      setIsLoading(true);

      // Simulate AI response
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content:
          "I've analyzed your request and here's what I found. This is a placeholder response that would normally contain insights from your data.",
        timestamp: new Date(),
      };

      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === threadId
            ? {
                ...conv,
                messages: [...conv.messages, assistantMessage],
                updatedAt: new Date(),
              }
            : conv
        )
      );

      setIsLoading(false);
    },
    [threadId]
  );

  return (
    <DataCoachChatContext.Provider
      value={{
        conversations,
        selectedConversation,
        isLoading,
        selectConversation,
        createNewConversation,
        sendMessage,
      }}
    >
      {children}
    </DataCoachChatContext.Provider>
  );
};

export const useDataCoachChat = () => {
  const context = useContext(DataCoachChatContext);
  if (!context) {
    throw new Error(
      'useDataCoachChat must be used within a DataCoachChatProvider'
    );
  }

  return context;
};
